import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, MenuItem, Select, FormControl, SelectChangeEvent, Icon } from "@mui/material";
import { useSelector } from "react-redux";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from "recharts";
import moment from "moment";

import { ThemeContext } from "../../context/Themes";
import { AnalyticsService } from "../../services";
import { Heading, Text } from "../elements";
import { formatAmountSuffix } from "../../utils/formatters";

const DARK_COLORS = {
  bg: "#1b2b4d",
  cardBg: "#172442",
  innerCardBg: "rgba(255, 255, 255, 0.03)",
  border: "#2f3f61",
  hoverBorder: "#39496b",
  title: "#f0f0f0",
  text: "#94a3b8",
  grid: "rgba(255, 255, 255, 0.05)",
  surfaceBase: "#1b2b4d",
  green: "#10b981",
  red: "#ef4444",
  blue: "#3b82f6",
};

const LIGHT_COLORS = {
  bg: "#f8fafc",
  cardBg: "#ffffff",
  innerCardBg: "#f1f5f9",
  border: "#e2e8f0",
  hoverBorder: "#cbd5e1",
  title: "#1e293b",
  text: "#64748b",
  grid: "rgba(0, 0, 0, 0.04)",
  surfaceBase: "#ffffff",
  green: "#059669",
  red: "#dc2626",
  blue: "#2563eb",
};

const INITIAL_FINANCIAL_STATE: FinancialDataState = {
  income: 0,
  expense: 0,
  netAmount: 0,
  isProfit: true,
  marginPct: 0,
};

export interface IncomeExpenseSeriesPoint {
  period?: string;
  income?: number | string;
  expense?: number | string;
  net?: number | string;
}

export interface IncomeExpenseSeriesResponse {
  granularity?: "day" | "month";
  series?: IncomeExpenseSeriesPoint[];
}

interface MonthlyIncomeExpenseChartProps {
  companyId?: string | number | null;
}

interface FinancialDataState {
  income: number;
  expense: number;
  netAmount: number;
  isProfit: boolean;
  marginPct: number;
}

interface MonthOption {
  value: string;
  label: string;
}

interface DailyTrendPoint {
  day: number;
  name: string;
  fullDate: string;
  income: number;
  expense: number;
}

interface ReduxState {
  user?: {
    user?: {
      company?: {
        id?: string | number;
      };
    };
  };
}

export const MonthlyIncomeExpenseChart: React.FC<MonthlyIncomeExpenseChartProps> = ({ companyId = null }) => {
  const { theme } = useContext(ThemeContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedMonth, setSelectedMonth] = useState<string>(moment().format("YYYY-MM"));

  const [financialData, setFinancialData] = useState<FinancialDataState>(INITIAL_FINANCIAL_STATE);
  const [chartTrendData, setChartTrendData] = useState<DailyTrendPoint[]>([]);

  const latestRequestId = useRef<number>(0);

  const userDetails = useSelector((state: ReduxState) => state.user?.user);
  const activeCompanyId = companyId || userDetails?.company?.id;

  const colors = theme !== "light_mode" ? DARK_COLORS : LIGHT_COLORS;

  const monthOptions: MonthOption[] = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const d = moment().subtract(i, "months");
      return {
        value: d.format("YYYY-MM"),
        label: d.format("MMMM YYYY"),
      };
    });
  }, []);

  const fetchMonthlyData = useCallback(async () => {
    const currentRequestId = ++latestRequestId.current;

    try {
      setLoading(true);

      const startMoment = moment(selectedMonth, "YYYY-MM").startOf("month");
      const endMoment = moment(selectedMonth, "YYYY-MM").endOf("month");

      // Server aggregates the daily buckets; one call, no pagination sweep.
      const response = await AnalyticsService.getCompanyIncomeExpenseSeries({
        fromDate: startMoment.toISOString(),
        toDate: endMoment.toISOString(),
        granularity: "day",
        ...(activeCompanyId !== undefined && activeCompanyId !== null ? { companyId: activeCompanyId } : {}),
      });

      if (currentRequestId !== latestRequestId.current) return;

      const series: IncomeExpenseSeriesPoint[] = response?.data?.series || [];

      let totalIncome = 0;
      let totalExpense = 0;

      const trendPoints: DailyTrendPoint[] = series.map((point) => {
        const income = Math.max(0, Number(point.income) || 0);
        const expense = Math.max(0, Number(point.expense) || 0);
        totalIncome += income;
        totalExpense += expense;

        const pointDate = moment(point.period, "YYYY-MM-DD");
        const day = pointDate.isValid() ? pointDate.date() : 0;

        return {
          day,
          name: `${day}`,
          fullDate: pointDate.isValid() ? pointDate.format("D MMM") : String(point.period ?? ""),
          income,
          expense,
        };
      });

      const netAmount = totalIncome - totalExpense;
      const marginPct = totalIncome > 0 ? (netAmount / totalIncome) * 100 : 0;

      if (currentRequestId === latestRequestId.current) {
        setFinancialData({
          income: totalIncome,
          expense: totalExpense,
          netAmount,
          isProfit: netAmount >= 0,
          marginPct,
        });

        setChartTrendData(trendPoints);
      }
    } catch (err) {
      if (currentRequestId === latestRequestId.current) {
        console.error("🚀 ~ fetchMonthlyData ~ error:", err);
        setFinancialData(INITIAL_FINANCIAL_STATE);
        setChartTrendData([]);
      }
    } finally {
      if (currentRequestId === latestRequestId.current) {
        setLoading(false);
      }
    }
  }, [activeCompanyId, selectedMonth]);

  useEffect(() => {
    fetchMonthlyData();
  }, [fetchMonthlyData]);

  const handleMonthChange = (e: SelectChangeEvent<string>) => {
    setSelectedMonth(e.target.value);
  };

  const hasData = financialData.income > 0 || financialData.expense > 0;

  return (
    <Box
      className="mc-card"
      sx={{
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: "16px",
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxShadow: theme === "light_mode" ? "0px 4px 16px rgba(0, 0, 0, 0.04)" : "none",
        transition: "all 0.3s ease",
      }}
    >
      {/* 1. Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          pb: 1,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: theme !== "light_mode" ? "#22355c" : `${colors.blue}1F`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon
              className="material-icons"
              style={{
                color: colors.blue,
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                lineHeight: 1,
                margin: 0,
                padding: 0,
              }}
            >
              trending_up
            </Icon>
          </Box>

          <Heading
            as="h2"
            style={{
              fontSize: "17px",
              fontWeight: 600,
              color: colors.title,
              whiteSpace: "nowrap",
            }}
          >
            Monthly Income & Expense
          </Heading>
        </Box>

        <FormControl size="small">
          <Select
            value={selectedMonth}
            onChange={handleMonthChange}
            sx={{
              color: colors.title,
              fontSize: "13px",
              fontWeight: 600,
              height: "34px",
              borderRadius: "8px",
              backgroundColor: colors.cardBg,
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: colors.border,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.hoverBorder,
              },
              ".MuiSvgIcon-root": { color: colors.title },
            }}
          >
            {monthOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} style={{ fontSize: "12px" }}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* 2. Content / Empty State Area */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            py: 6,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : hasData ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            mt: 0.5,
          }}
        >
          {/* Daily Line Chart */}
          <Box sx={{ width: "100%", flex: 1, minHeight: 280, mt: 0.5 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartTrendData}
                margin={{
                  top: 10,
                  right: 15,
                  left: -15,
                  bottom: 0,
                }}
              >
                <CartesianGrid strokeDasharray="2 2" stroke={colors.grid} vertical={false} />

                <XAxis
                  dataKey="name"
                  stroke={colors.text}
                  tick={{ fontSize: 10, fill: colors.text }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  ticks={["1", "5", "10", "15", "20", "25", `${chartTrendData.length}`]}
                />

                <YAxis
                  width={65}
                  stroke={colors.text}
                  tickFormatter={(val) => formatAmountSuffix(val)}
                  tick={{ fontSize: 10, fill: colors.text }}
                  tickCount={4}
                  axisLine={false}
                  tickLine={false}
                />

                <RechartsTooltip
                  cursor={{
                    stroke: colors.border,
                    strokeWidth: 1,
                    strokeDasharray: "3 3",
                  }}
                  labelFormatter={(_, payload) => {
                    const item = payload?.[0]?.payload;
                    return item ? item.fullDate : "";
                  }}
                  formatter={(val: number) => [formatAmountSuffix(val, true), ""]}
                  contentStyle={{
                    backgroundColor: colors.cardBg,
                    borderColor: colors.border,
                    borderRadius: "8px",
                    color: colors.title,
                    fontSize: "12px",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                  }}
                />

                <Legend
                  wrapperStyle={{
                    fontSize: "11px",
                    paddingTop: "2px",
                  }}
                />

                <Line
                  type="monotone"
                  dataKey="income"
                  name="Income"
                  stroke={colors.blue}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: colors.blue,
                    stroke: colors.surfaceBase,
                    strokeWidth: 2,
                  }}
                />

                <Line
                  type="monotone"
                  dataKey="expense"
                  name="Expense"
                  stroke={colors.red}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: colors.red,
                    stroke: colors.surfaceBase,
                    strokeWidth: 2,
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* 3. Bottom Stat Cards */}
          <Box
            sx={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
              mt: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                p: "8px 12px",
                borderRadius: "10px",
                backgroundColor: colors.innerCardBg,
                border: `1px solid ${colors.border}`,
                transition: "all 0.2s ease",
                "&:hover": { borderColor: colors.blue },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    backgroundColor: colors.blue,
                  }}
                />
                <Text
                  style={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: colors.text,
                  }}
                >
                  Total Income
                </Text>
              </Box>
              <Text
                style={{
                  fontSize: "15px",
                  fontWeight: 700,
                  color: colors.title,
                }}
              >
                {formatAmountSuffix(financialData.income, true)}
              </Text>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                p: "8px 12px",
                borderRadius: "10px",
                backgroundColor: colors.innerCardBg,
                border: `1px solid ${colors.border}`,
                transition: "all 0.2s ease",
                "&:hover": { borderColor: colors.red },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    backgroundColor: colors.red,
                  }}
                />
                <Text
                  style={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: colors.text,
                  }}
                >
                  Total Expense
                </Text>
              </Box>
              <Text
                style={{
                  fontSize: "15px",
                  fontWeight: 700,
                  color: colors.title,
                }}
              >
                {formatAmountSuffix(financialData.expense, true)}
              </Text>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text style={{ color: colors.text }}>No income or expense data available</Text>
        </Box>
      )}
    </Box>
  );
};

export default MonthlyIncomeExpenseChart;
